import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { z } from "zod";
import { createPhoneSchema } from "@/lib/validation/phone";

export const createAuthSchemas = (locale: Locales) => {
  const titleSchema = z.enum(["mademoiselle", "madame", "monsieur"], {
    required_error: __("Civility is required", locale),
  });

  const firstNameSchema = z
    .string({
      required_error: __("First name is required", locale),
    })
    .trim()
    .min(3, __("Invalid First name", locale));

  const lastNameSchema = z
    .string({
      required_error: __("Last name is required", locale),
    })
    .trim()
    .min(3, __("Invalid Last name", locale));

  const emailSchema = z
    .string({
      required_error: __("Email is required", locale),
    })
    .trim()
    .email(__("Invalid email", locale));

  const phoneSchema = createPhoneSchema({
    required: __("Phone number is required", locale),
    invalid: __("Invalid phone number", locale),
  });

  const birthDateSchema = z.string().optional();

  const passwordSchema = z
    .string({
      required_error: __("Password is required", locale),
    })
    .trim()
    .min(8, __("Invalid Password", locale));

  const confirmPasswordSchema = z
    .string({
      required_error: __("Password confirmation is required", locale),
    })
    .trim()
    .min(8, __("Invalid Password", locale));

  const passwordsMatchRefine = (data: { password: string; confirmPassword: string }) =>
    data.password === data.confirmPassword;

  const passwordValidationObject = z.object({
    password: passwordSchema,
    confirmPassword: confirmPasswordSchema,
  });

  const signupSchema = z
    .object({
      title: titleSchema,
      firstName: firstNameSchema,
      lastName: lastNameSchema,
      email: emailSchema,
      phone: phoneSchema,
      birthDate: birthDateSchema,
      subscribeToNewsletter: z.boolean().optional(),
    })
    .merge(passwordValidationObject)
    .refine(passwordsMatchRefine, {
      message: __("Passwords don't match", locale),
      path: ["confirmPassword"],
    });

  const signinSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
  });

  const resetPasswordSchema = z.object({
    email: emailSchema,
  });

  const newPasswordSchema = passwordValidationObject.refine(passwordsMatchRefine, {
    message: __("Passwords don't match", locale),
    path: ["confirmPassword"],
  });

  return {
    signupSchema,
    signinSchema,
    resetPasswordSchema,
    newPasswordSchema,
  };
};

export type SignupSchema = z.infer<ReturnType<typeof createAuthSchemas>["signupSchema"]>;
export type SigninSchema = z.infer<ReturnType<typeof createAuthSchemas>["signinSchema"]>;
export type ResetPasswordSchema = z.infer<ReturnType<typeof createAuthSchemas>["resetPasswordSchema"]>;
export type NewPasswordSchema = z.infer<ReturnType<typeof createAuthSchemas>["newPasswordSchema"]>;
